import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  PersonAddAltRounded,
  EditRounded,
  DeleteOutlineRounded,
  KeyRounded,
  VisibilityRounded,
  SensorsRounded,
  PeopleAltRounded,
} from '@mui/icons-material';
import api, { errorMessage } from '../lib/api';
import { relativeTime, formatDateTime } from '../lib/format';
import { palette } from '../theme';
import { GlassPanel, SectionTitle, StatusPill, EmptyState, PanelLoader } from '../components/ui/GlassPanel';
import DataTable from '../components/data/DataTable';
import { useToast } from '../components/ui/ToastProvider';

const EMPTY_FORM = {
  user_id: '',
  full_name: '',
  email: '',
  phone: '',
  password: '',
  confirm_password: '',
  role: 'user',
  status: true,
};

export default function Users() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', target: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detail, setDetail] = useState({ open: false, user: null, devices: [] });
  const [tableReset, setTableReset] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users/directory');
      setUsers(response.data || []);
    } catch (error) {
      toast.error(errorMessage(error, 'Unable to load the operator directory.'));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setDialog({ open: true, mode: 'create', target: null });
  };

  const openEdit = (user) => {
    setForm({
      user_id: user.username,
      full_name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '',
      confirm_password: '',
      role: user.role,
      status: user.is_active,
    });
    setErrors({});
    setDialog({ open: true, mode: 'edit', target: user });
  };

  const validate = () => {
    const next = {};
    if (!form.user_id.trim()) next.user_id = 'Operator ID is required';
    if (!form.full_name.trim()) next.full_name = 'Full name is required';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = 'A valid email address is required';
    if (dialog.mode === 'create') {
      if (!form.password || form.password.length < 6) next.password = 'Minimum 6 characters';
      if (form.password !== form.confirm_password) next.confirm_password = 'Passwords do not match';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (dialog.mode === 'create') {
        await api.post('/admin/users', {
          user_id: form.user_id.trim(),
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          password: form.password,
          role: form.role,
          status: form.status,
        });
        toast.success(`Operator ${form.user_id.trim()} provisioned.`);
      } else {
        await api.patch(`/admin/users/${dialog.target.id}`, {
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          role: form.role,
          status: form.status,
        });
        toast.success(`Operator ${form.user_id.trim()} updated.`);
      }
      setDialog({ open: false, mode: 'create', target: null });
      load();
      setTableReset((current) => current + 1);
    } catch (error) {
      toast.error(errorMessage(error, 'The operator could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`Operator ${deleteTarget.username} removed.`);
      setDeleteTarget(null);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'The operator could not be deleted.'));
    }
  };

  const handleResetPassword = (user) => {
    const generated = `Bn${Math.random().toString(36).slice(2, 8)}!`;
    // eslint-disable-next-line no-alert
    const confirmed = window.prompt(
      `Reset the passphrase for ${user.username}.\nEnter the new passphrase or leave blank to auto-generate one:`,
      generated
    );
    if (confirmed === null) return;
    const password = confirmed.trim() || generated;
    api
      .post(`/auth/users/${user.id}/reset-password`, { new_password: password })
      .then(() => toast.success(`Passphrase rotated for ${user.username}.`))
      .catch((error) => toast.error(errorMessage(error, 'The passphrase could not be reset.')));
  };

  const openDetail = async (user) => {
    setDetail({ open: true, user, devices: [] });
    try {
      const response = await api.get(`/admin/users/${user.id}/devices`);
      setDetail({ open: true, user, devices: response.data || [] });
    } catch (error) {
      /* device listing is optional context */
    }
  };

  const columns = [
    {
      key: 'username',
      label: 'User ID',
      width: 160,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              background: alpha(palette.secondary, 0.14),
              border: `1px solid ${alpha(palette.secondary, 0.3)}`,
            }}
          >
            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: palette.secondary }}>
              {row.username.slice(0, 2).toUpperCase()}
            </Typography>
          </Box>
          <Typography className="mono" sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
            {row.username}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'full_name',
      label: 'Name',
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{row.full_name}</Typography>
          {row.phone && (
            <Typography className="mono" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              {row.phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (row) => (
        <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>{row.email}</Typography>
      ),
    },
    {
      key: 'device_count',
      label: 'Assigned Devices',
      align: 'center',
      render: (row) => (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1.1,
            py: 0.4,
            borderRadius: 2,
            background: alpha(palette.primary, 0.1),
            border: `1px solid ${alpha(palette.primary, 0.22)}`,
          }}
        >
          <SensorsRounded sx={{ fontSize: 13, color: palette.primary }} />
          <Typography className="mono" sx={{ fontSize: '0.74rem', fontWeight: 600 }}>
            {row.device_count}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      render: (row) => (
        <StatusPill label={row.role} tone={row.role === 'admin' ? 'secondary' : 'info'} pulse={false} />
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => (
        <StatusPill label={row.is_active ? 'active' : 'suspended'} tone={row.is_active ? 'success' : 'danger'} />
      ),
    },
    {
      key: 'last_login',
      label: 'Last Login',
      render: (row) => (
        <Tooltip title={formatDateTime(row.last_login)}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {relativeTime(row.last_login)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View profile">
            <IconButton size="small" onClick={() => openDetail(row)} sx={{ color: 'text.secondary' }}>
              <VisibilityRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit operator">
            <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: palette.primary }}>
              <EditRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset passphrase">
            <IconButton size="small" onClick={() => handleResetPassword(row)} sx={{ color: palette.warning }}>
              <KeyRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete operator">
            <IconButton size="small" onClick={() => setDeleteTarget(row)} sx={{ color: palette.danger }}>
              <DeleteOutlineRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="User Directory"
          subtitle="Provision operators, control access and assign endpoints"
          icon={<PeopleAltRounded sx={{ fontSize: 18 }} />}
          action={
            <Button variant="contained" startIcon={<PersonAddAltRounded />} onClick={openCreate}>
              Create User
            </Button>
          }
        />
      </GlassPanel>

      <GlassPanel>
        {loading ? (
          <PanelLoader label="Loading operator directory" />
        ) : (
          <DataTable
            columns={columns}
            rows={users}
            rowKey={(row) => row.id}
            searchKeys={['username', 'full_name', 'email', 'phone']}
            searchPlaceholder="Search operators…"
            filters={[
              {
                key: 'role',
                label: 'Role',
                options: [
                  { value: 'admin', label: 'Administrator' },
                  { value: 'user', label: 'Operator' },
                ],
              },
              {
                key: 'is_active',
                label: 'Status',
                options: [
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Suspended' },
                ],
              },
            ]}
            resetKey={tableReset}
            emptyIcon={<PeopleAltRounded />}
            emptyTitle="No operators registered"
            emptyDescription="Create the first operator account to grant device access."
            pageSize={8}
          />
        )}
      </GlassPanel>

      {/* create / edit dialog */}
      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, mode: 'create', target: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialog.mode === 'create' ? 'Create Operator Account' : `Edit ${dialog.target?.username}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="User ID"
                  value={form.user_id}
                  disabled={dialog.mode === 'edit'}
                  onChange={(event) => setForm({ ...form, user_id: event.target.value })}
                  error={Boolean(errors.user_id)}
                  helperText={errors.user_id}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Full name"
                  value={form.full_name}
                  onChange={(event) => setForm({ ...form, full_name: event.target.value })}
                  error={Boolean(errors.full_name)}
                  helperText={errors.full_name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone"
                  value={form.phone}
                  onChange={(event) => setForm({ ...form, phone: event.target.value })}
                  placeholder="+91 00000 00000"
                />
              </Grid>

              {dialog.mode === 'create' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="password"
                      label="Password"
                      value={form.password}
                      onChange={(event) => setForm({ ...form, password: event.target.value })}
                      error={Boolean(errors.password)}
                      helperText={errors.password || 'Minimum 6 characters'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="password"
                      label="Confirm password"
                      value={form.confirm_password}
                      onChange={(event) => setForm({ ...form, confirm_password: event.target.value })}
                      error={Boolean(errors.confirm_password)}
                      helperText={errors.confirm_password}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select
                    label="Role"
                    value={form.role}
                    onChange={(event) => setForm({ ...form, role: event.target.value })}
                  >
                    <MenuItem value="user">Operator</MenuItem>
                    <MenuItem value="admin">Administrator</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.status}
                      onChange={(event) => setForm({ ...form, status: event.target.checked })}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.82rem' }}>Account active</Typography>}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <Divider sx={{ borderColor: alpha(palette.primary, 0.1) }} />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialog({ open: false, mode: 'create', target: null })} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : dialog.mode === 'create' ? 'Create operator' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete operator</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.6 }}>
            Remove <Box component="span" className="mono" sx={{ color: 'text.primary' }}>{deleteTarget?.username}</Box> from
            the platform? Unassigned endpoints must be reassigned before the account can be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete account
          </Button>
        </DialogActions>
      </Dialog>

      {/* operator detail */}
      <Dialog open={detail.open} onClose={() => setDetail({ open: false, user: null, devices: [] })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Operator profile</DialogTitle>
        <DialogContent>
          {detail.user && (
            <Box sx={{ display: 'grid', gap: 1.25, pt: 0.5 }}>
              <Grid container spacing={1.5}>
                {[
                  { label: 'User ID', value: detail.user.username },
                  { label: 'Name', value: detail.user.full_name },
                  { label: 'Email', value: detail.user.email },
                  { label: 'Phone', value: detail.user.phone || '—' },
                  { label: 'Role', value: detail.user.role },
                  { label: 'Last login', value: relativeTime(detail.user.last_login) },
                ].map((field) => (
                  <Grid item xs={12} sm={6} key={field.label}>
                    <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.14em', color: 'text.secondary' }}>
                      {field.label.toUpperCase()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mt: 0.25 }}>{field.value}</Typography>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 0.5 }} />

              <Typography sx={{ fontSize: '0.72rem', letterSpacing: '0.14em', color: 'text.secondary' }}>
                ASSIGNED DEVICES
              </Typography>
              {detail.devices.length === 0 ? (
                <EmptyState title="No devices assigned" description="Assign endpoints from the device console." minHeight={120} />
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {detail.devices.map((device) => (
                    <Box
                      key={device.device_id}
                      sx={{
                        px: 1.4,
                        py: 0.7,
                        borderRadius: 2,
                        background: alpha(palette.primary, 0.08),
                        border: `1px solid ${alpha(palette.primary, 0.2)}`,
                      }}
                    >
                      <Typography className="mono" sx={{ fontSize: '0.72rem' }}>
                        {device.device_id}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDetail({ open: false, user: null, devices: [] })} sx={{ color: 'text.secondary' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
